import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { getRequest } from "src/api/inventory";
import { DataTable, StatusBadge, type Column } from "src/components/common";
import { renderNumber } from "src/lib/utils";
import type { StockRequestItem } from "src/types/inventory";

const columns: Column<StockRequestItem>[] = [
	{ key: "name", header: "Product", cell: (row) => row.name ?? "N/A", primary: true },
	{ key: "requested", header: "Requested", cell: (row) => renderNumber(row.requested_quantity) },
	{ key: "approved", header: "Approved", cell: (row) => renderNumber(row.approved_quantity) },
];

const SummaryRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
	<div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-border tw-py-2.5 last:tw-border-0">
		<span className="tw-text-[13px] tw-text-text-soft">{label}</span>
		<span className="tw-text-right tw-text-[13.5px] tw-font-medium tw-capitalize tw-text-text">{value}</span>
	</div>
);

interface RequestDetailProps {
	requestUUID: string;
}

const RequestDetail = ({ requestUUID }: RequestDetailProps) => {
	const { data } = useQuery({
		queryKey: ["requests", requestUUID],
		queryFn: () => getRequest(requestUUID),
		suspense: true,
	});

	const details = data?.data;

	return (
		<div className="tw-flex tw-flex-col tw-gap-5">
			<div className="tw-rounded-[10px] tw-border tw-border-border tw-px-4">
				<SummaryRow label="From" value={details?.from_name || "N/A"} />
				<SummaryRow label="To" value={details?.to_name || "N/A"} />
				<SummaryRow label="Status" value={details?.status ? <StatusBadge status={details.status} /> : "N/A"} />
				<SummaryRow label="Date" value={details?.created_at ? format(new Date(details.created_at), "PPP") : "N/A"} />
			</div>

			<DataTable
				columns={columns}
				data={details?.items ?? []}
				emptyTitle="No items"
				emptyDescription="This request has no line items."
			/>
		</div>
	);
};

export default RequestDetail;
